/**
 * useOfflineQueue — React hook that exposes `{online, pending, flushing, flushNow}`
 * and auto-flushes the queue whenever the browser fires `online`.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { listQueue, queueSize, flushQueue, onQueueChange } from '@/lib/offlineQueue';
import logger from '@/lib/logger';

async function defaultSender(action) {
  if (action.kind === 'lead-note') {
    await api.addNote(action.leadId, action.payload.content);
    return;
  }
  throw new Error(`Unknown offline action kind: ${action.kind}`);
}

export function useOfflineQueue({ sender = defaultSender } = {}) {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const flushingRef = useRef(false);

  const refreshCount = useCallback(() => {
    queueSize().then(setPending).catch(() => {});
  }, []);

  const flushNow = useCallback(async () => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    setFlushing(true);
    try {
      const { flushed, remaining } = await flushQueue(sender);
      setPending(remaining);
      if (flushed > 0) {
        // Notify components (e.g. lead detail) to refetch
        window.dispatchEvent(new CustomEvent('offline-queue:flushed', { detail: { flushed } }));
      }
    } catch (e) {
      logger.error('flushQueue failed', e);
    }
    setFlushing(false);
    flushingRef.current = false;
  }, [sender]);

  useEffect(() => {
    refreshCount();
    const unsub = onQueueChange(refreshCount);

    const onOnline = () => {
      setOnline(true);
      flushNow();
    };
    const onOffline = () => setOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      unsub();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [flushNow, refreshCount]);

  return { online, pending, flushing, flushNow, listQueue };
}
